// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  type SchemaValidationOptions,
  handleSchemaValidationError,
} from '@/utilities/schema-validation';
import { ZodError, ZodSchema, z } from 'zod';

/**
 * Configuration options for response parsing.
 */
type ParseResponseOptions = SchemaValidationOptions;

/**
 * Parses an API response against a Zod schema with enhanced error logging.
 *
 * When parsing fails, this function:
 * 1. Logs the full ZodError details to the console in development
 * 2. Sends the error to Sentry with context about which schema and endpoint failed
 * 3. Re-throws a descriptive error that can be displayed to users
 *
 * @param schema - The Zod schema to validate against
 * @param data - The API response data to parse
 * @param options - Configuration options including schema name and endpoint
 * @returns The parsed and validated data
 * @throws Error with descriptive message if parsing fails
 */
export async function parseResponse<T extends ZodSchema>(
  schema: T,
  data: unknown,
  options: ParseResponseOptions
): Promise<z.infer<T>> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return await schema.parseAsync(data);
  } catch (error) {
    if (error instanceof ZodError) {
      handleSchemaValidationError(error, data, options);
    }

    // Re-throw non-Zod errors
    throw error;
  }
}
